import { useEffect, useRef, useState } from 'react'

/**
 * 打字机效果：目标文本（流式快照）增长时按帧逐步追平，积压越多每帧出字越多，
 * 保证 SSE 分批到达时用户看到的仍是逐字平滑输出而非整段跳变。
 *
 * 切屏返回策略：标签页隐藏时流仍在后台累积，回来时若积压过大（超过阈值），
 * 历史内容直接整块显示、不重放补字，只对活跃尾部继续逐字输出。
 *
 * @param target  完整目标文本（累积快照）
 * @param enabled 是否启用追字；为 false 时直接显示完整目标文本
 * @returns 当前应展示的文本
 */
export function useTypewriter(target: string, enabled: boolean): string {
  // 初始即为 target：重挂载（如切换会话后返回）不会重放动画
  const [shown, setShown] = useState(target)
  const shownRef = useRef(shown)
  shownRef.current = shown

  useEffect(() => {
    if (!enabled) {
      if (shownRef.current !== target) setShown(target)
      return
    }
    if (shownRef.current === target) return
    // 目标被替换（非追加，如重试清空重发）时放弃追字，直接同步
    if (!target.startsWith(shownRef.current)) {
      setShown(target)
      return
    }
    // 积压过大（如切屏期间后台持续输出）：快进到只留活跃尾部，历史内容直接显示
    const lag = target.length - shownRef.current.length
    if (lag > LAG_SNAP_CHARS) {
      const snapped = target.slice(0, Math.max(0, target.length - LIVE_TAIL_CHARS))
      shownRef.current = snapped
      setShown(snapped)
    }
    let raf = 0
    const tick = () => {
      const current = shownRef.current
      if (!target.startsWith(current) || current.length >= target.length) {
        setShown(target)
        return
      }
      const remaining = target.length - current.length
      // 追赶策略：积压越大出字越快，帧率约 60fps 下 60~480 字/秒
      const step = Math.max(1, Math.min(8, Math.ceil(remaining / 10)))
      setShown(target.slice(0, current.length + step))
      raf = window.requestAnimationFrame(tick)
    }
    raf = window.requestAnimationFrame(tick)
    return () => window.cancelAnimationFrame(raf)
  }, [target, enabled])

  return shown
}

/** 积压超过该字符数视为“离开期间积攒的历史”，直接快进不再补放 */
const LAG_SNAP_CHARS = 160
/** 快进后仍保留逐字输出的活跃尾部长度 */
const LIVE_TAIL_CHARS = 24
